import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaTwitch, FaCheckCircle, FaBan, FaMinus } from "react-icons/fa";
import {
	Match,
	PlayerStats,
	CombinedMapStats,
	getWinRateClass,
	getAvatarUrl,
} from "../pages/MatchPage";
import VetoSection from "./match/VetoSection";
import { parseVetoLog } from "../utils/vetoParser";
import { useMappoolContext } from "../context/MappoolContext";

interface UpcomingMatchViewProps {
	match: Match;
	team1Players: PlayerStats[];
	team2Players: PlayerStats[];
	mapStats: CombinedMapStats[];
}

const UpcomingMatchView: React.FC<UpcomingMatchViewProps> = ({
	match,
	team1Players,
	team2Players,
	mapStats,
}) => {
	const [showStream, setShowStream] = useState<boolean>(true);
	const [isTwitchLoaded, setIsTwitchLoaded] = useState<boolean>(false);
	const [currentSeasonMaps, setCurrentSeasonMaps] = useState<string[]>([]);
	const { getMappool } = useMappoolContext();

	// Load mappool for the match's season
	useEffect(() => {
		const loadMappool = async () => {
			if (match && match.season) {
				try {
					const seasonMappool = await getMappool(match.season);
					setCurrentSeasonMaps(seasonMappool);
				} catch (error) {
					console.error('Error loading mappool:', error);
					// Fallback to empty array on error
					setCurrentSeasonMaps([]);
				}
			}
		};
		loadMappool();
	}, [match, getMappool]);

	// Parse veto data if available
	const mapPool = currentSeasonMaps;
	const vetoSteps = match?.veto && match.team1 && match.team2
		? parseVetoLog(match.veto, match.team1, match.team2)
		: [];

	// Map images - comprehensive list including all possible maps
	const mapImages: Record<string, string> = {
		Mirage: "/images/maps/de_mirage.png",
		Nuke: "/images/maps/de_nuke.png",
		Inferno: "/images/maps/de_inferno.png",
		Dust2: "/images/maps/de_dust2.png",
		Overpass: "/images/maps/de_overpass.png",
		Ancient: "/images/maps/de_ancient.png",
		Anubis: "/images/maps/de_anubis.png",
		Train: "/images/maps/de_train.png",
		Vertigo: "/images/maps/de_vertigo.png",
		Cache: "/images/maps/de_cache.png",
		Cobblestone: "/images/maps/de_cbble.png",
	};

	// Helper function to get map image URL
	const getMapImageUrl = (mapName: string): string => {
		// Check if we have a direct match
		if (mapImages[mapName]) {
			return mapImages[mapName];
		}

		// Try to match with de_ prefix
		const withPrefix = `de_${mapName.toLowerCase()}`;
		const matchingKey = Object.keys(mapImages).find(
			(key) =>
				key.toLowerCase() === mapName.toLowerCase() ||
				key.toLowerCase() === withPrefix
		);

		if (matchingKey) {
			return mapImages[matchingKey];
		}

		// Default fallback
		return "/images/maps/default.jpg";
	};

	// Helper function to render veto stats with icons
	const renderVetoStats = (teamStats: any) => {
		const picks = teamStats.amountPicked || 0;
		const bans = teamStats.amountBanned || 0;

		if (picks === 0 && bans === 0) {
			return (
				<div className="veto-stats-container">
					<div className="veto-stat-item no-activity">
						<span className="veto-label">P/B:</span>
						<span className="veto-text">No activity</span>
					</div>
				</div>
			);
		}

		// Single line with picks and bans
		const items = [];
		if (picks > 0) {
			items.push(
				<React.Fragment key="pick">
					<FaCheckCircle className="veto-icon pick-icon" />
					<span className="veto-count">{picks}</span>
				</React.Fragment>
			);
		}
		if (bans > 0) {
			items.push(
				<React.Fragment key="ban">
					<FaBan className="veto-icon ban-icon" />
					<span className="veto-count">{bans}</span>
				</React.Fragment>
			);
		}

		return (
			<div className="veto-stats-container">
				<div className="veto-stat-item combined">
					<span className="veto-label">P/B:</span>
					{items}
				</div>
			</div>
		);
	};

	// Filter mapStats to only show maps from the current season's mappool
	// Make filtering case-insensitive and handle potential naming variations
	const filteredMapStats = mapStats.filter((map) => {
		const mapName = map.mapName;

		// Check for exact match (case-insensitive)
		const exactMatch = currentSeasonMaps.some(
			(seasonMap) => seasonMap.toLowerCase() === mapName.toLowerCase()
		);

		// Check for match without "de_" prefix
		const withoutPrefix = currentSeasonMaps.some(
			(seasonMap) =>
				seasonMap.toLowerCase() === mapName.toLowerCase().replace("de_", "")
		);

		// Check for match with "de_" prefix added
		const withPrefix = currentSeasonMaps.some(
			(seasonMap) => `de_${seasonMap.toLowerCase()}` === mapName.toLowerCase()
		);

		return exactMatch || withoutPrefix || withPrefix;
	});

	useEffect(() => {
		// Only proceed if we have a stream URL and want to show the stream
		if (!match?.stream_url || !showStream) {
			return;
		}

		let channelName = "";
		let mountedRef = true;

		try {
			// Extract channel name from Twitch URL
			const url = new URL(match.stream_url);
			if (url.hostname === "www.twitch.tv" || url.hostname === "twitch.tv") {
				channelName = url.pathname.replace("/", "");
			}
		} catch (error) {
			console.error("Error parsing Twitch URL:", error);
			return;
		}

		if (!channelName) {
			// No valid channel name found
			const container = document.getElementById("twitch-embed");
			if (container) {
				container.innerHTML = `
          <div class="twitch-fallback">
            <p>Unable to embed stream directly.</p>
            <a href="${match.stream_url}" target="_blank" rel="noopener noreferrer" class="stream-link">
              <span>Watch on Twitch</span>
            </a>
          </div>
        `;
			}
			return;
		}

		// Clean up any existing Twitch scripts to avoid duplicates
		const existingScripts = document.querySelectorAll(
			'script[src="https://embed.twitch.tv/embed/v1.js"]'
		);
		existingScripts.forEach((script) => script.remove());

		// Create and load the Twitch embed script
		const script = document.createElement("script");
		script.src = "https://embed.twitch.tv/embed/v1.js";
		script.async = true;

		script.onload = () => {
			if (!mountedRef) return;

			setTimeout(() => {
				try {
					const twitchDiv = document.getElementById("twitch-embed");
					if (!twitchDiv) {
						console.error('Could not find element with id "twitch-embed"');
						return;
					}

					// Clear any existing content
					twitchDiv.innerHTML = "";

					// Initialize the Twitch embed
					if (window.Twitch && window.Twitch.Embed) {
						new window.Twitch.Embed("twitch-embed", {
							width: "100%",
							height: 400,
							channel: channelName,
							layout: "video",
							autoplay: true,
							muted: false,
							parent: [window.location.hostname],
						});

						setIsTwitchLoaded(true);
					} else {
						console.error("Twitch embed API not available");
					}
				} catch (error) {
					console.error("Error initializing Twitch embed:", error);
				}
			}, 100); // Small delay to ensure DOM is ready
		};

		script.onerror = (e) => {
			console.error("Error loading Twitch script:", e);
		};

		document.body.appendChild(script);

		// Clean up on unmount
		return () => {
			mountedRef = false;
			setIsTwitchLoaded(false);

			// Remove all Twitch scripts
			document
				.querySelectorAll('script[src="https://embed.twitch.tv/embed/v1.js"]')
				.forEach((el) => el.remove());

			// Clear the container
			const embedElement = document.getElementById("twitch-embed");
			if (embedElement) {
				embedElement.innerHTML = "";
			}
		};
	}, [match?.stream_url, showStream]);

	// Check if this is a TBD match (placeholder teams)
	const isTBDMatch = match?.team1?.teamName === "TBD" || match?.team2?.teamName === "TBD";

	return (
		<>
		<div className="container">
			<div className="match-three-column">
				{/* Left Column - Team 1 Players (hide for TBD matches) */}
				<div className="team-players team1-players">
					{!isTBDMatch && team1Players.map((player, index) => (
					<div key={player.steamID || index} className="player-card">
						<div className="player-avatar">
							<Link
								to={`/profile/${player.steamID}`}
								style={{ display: "block" }}
							>
								<img
									src={player.avatarUrl}
									alt={player.playerName}
									className="avatar-img"
									onError={(e) => {
										const target = e.target as HTMLImageElement;
										const powerStatsUrl = `https://api.powerstats.dk/PowerStats/api/Account/Profile/${player.steamID}?width=400`;

										target.onerror = () => {
											target.onerror = null;
											target.src =
												"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath d='M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 128c39.77 0 72 32.23 72 72S295.8 272 256 272c-39.77 0-72-32.23-72-72S216.2 128 256 128zM256 448c-52.93 0-100.9-21.53-135.7-56.29C136.5 349.9 176.5 320 224 320h64c47.54 0 87.54 29.88 103.7 71.71C356.9 426.5 308.9 448 256 448z'/%3E%3C/svg%3E";
										};

										target.src = powerStatsUrl;
									}}
								/>
							</Link>
						</div>
						<div className="player-info">
							<Link
								to={`/profile/${player.steamID}`}
								style={{ textDecoration: "none", color: "inherit" }}
							>
								<div className="player-name">{player.playerName}</div>
							</Link>
							<div className="player-rating">
								Rating: {player.rating && player.rating > 0 ? player.rating.toFixed(2) : 'N/A'}
							</div>
						</div>
					</div>
				))}
			</div>

			{/* Middle Column - Contains Stream + Maps */}
			<div className="middle-column">
				{/* Stream Section - Only show if available */}
				{match.stream_url && (
					<div className="match-stream-section">
						{showStream ? (
							<>
								{/* Stream header with minimize button */}
								<div className="stream-header">
									<h3>Live Stream</h3>
									<button
										className="stream-minimize-button"
										onClick={() => setShowStream(false)}
									>
										<span>Minimize</span>
									</button>
								</div>

								{/* Stream content */}
								<div className="stream-content">
									<div id="twitch-embed" className="twitch-container"></div>
								</div>
							</>
						) : (
							/* Show stream button when minimized */
							<button
								className="stream-restore-button"
								onClick={() => setShowStream(true)}
							>
								<FaTwitch /> <span>Show Stream</span>
							</button>
						)}
					</div>
				)}

				{/* Maps Section */}
				<div className="match-maps">
					{filteredMapStats.length > 0 ? (
						filteredMapStats.map((map, index) => (
							<div key={index} className="map-row">
								{/* Team 1 Map Stats */}
								<div className="team1-map-stats">
									<div className="map-stat veto-stat">
										{renderVetoStats(map.team1)}
									</div>
									<div className="map-stat win-stat">
										{map.team1.amountPlayed > 0 ? (
											<>
												<span className="stat-label">W/R:</span>
												<span
													className={`win-rate ${getWinRateClass(
														map.team1.amountWon / map.team1.amountPlayed
													)}`}
												>
													{Math.round(
														(map.team1.amountWon / map.team1.amountPlayed) * 100
													)}
													%
												</span>
												<span className="played-count">
													({map.team1.amountWon}/{map.team1.amountPlayed})
												</span>
											</>
										) : (
											<span className="not-played">
												<span className="stat-label">W/R:</span>
												<span className="x-mark">✕</span>
											</span>
										)}
									</div>
								</div>

								<div className="map-middle">
									<div className="map-image">
										<img
											src={getMapImageUrl(map.mapName)}
											alt={map.mapName}
											className="map-img"
											onError={(e) => {
												(e.target as HTMLImageElement).src =
													"/images/maps/default.jpg";
											}}
										/>
										<div className="map-name">{map.mapName}</div>
									</div>
								</div>

								{/* Team 2 Map Stats */}
								<div className="team2-map-stats">
									<div className="map-stat veto-stat">
										{renderVetoStats(map.team2)}
									</div>
									<div className="map-stat win-stat">
										{map.team2.amountPlayed > 0 ? (
											<>
												<span className="stat-label">W/R:</span>
												<span
													className={`win-rate ${getWinRateClass(
														map.team2.amountWon / map.team2.amountPlayed
													)}`}
												>
													{Math.round(
														(map.team2.amountWon / map.team2.amountPlayed) * 100
													)}
													%
												</span>
												<span className="played-count">
													({map.team2.amountWon}/{map.team2.amountPlayed})
												</span>
											</>
										) : (
											<span className="not-played">
												<span className="stat-label">W/R:</span>
												<span className="x-mark">✕</span>
											</span>
										)}
									</div>
								</div>
							</div>
						))
					) : (
						<div className="no-maps">
							<p>No map data available for this match</p>
						</div>
					)}

					{/* No stream message - moved here below the map veto section */}
					{!match.stream_url && (
						<div className="no-stream">
							<FaTwitch />
							<span>No stream available for this match</span>
						</div>
					)}
				</div>
			</div>

			{/* Right Column - Team 2 Players (hide for TBD matches) */}
			<div className="team-players team2-players">
				{!isTBDMatch && team2Players.map((player, index) => (
					<div key={player.steamID || index} className="player-card">
						<div className="player-info">
							<Link
								to={`/profile/${player.steamID}`}
								style={{ textDecoration: "none", color: "inherit" }}
							>
								<div className="player-name">{player.playerName}</div>
							</Link>
							<div className="player-rating">
								Rating: {player.rating && player.rating > 0 ? player.rating.toFixed(2) : 'N/A'}
							</div>
						</div>
						<div className="player-avatar">
							<Link
								to={`/profile/${player.steamID}`}
								style={{ display: "block" }}
							>
								<img
									src={player.avatarUrl}
									alt={player.playerName}
									className="avatar-img"
									onError={(e) => {
										const target = e.target as HTMLImageElement;
										const powerStatsUrl = `https://api.powerstats.dk/PowerStats/api/Account/Profile/${player.steamID}?width=400`;

										target.onerror = () => {
											target.onerror = null;
											target.src =
												"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath d='M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 128c39.77 0 72 32.23 72 72S295.8 272 256 272c-39.77 0-72-32.23-72-72S216.2 128 256 128zM256 448c-52.93 0-100.9-21.53-135.7-56.29C136.5 349.9 176.5 320 224 320h64c47.54 0 87.54 29.88 103.7 71.71C356.9 426.5 308.9 448 256 448z'/%3E%3C/svg%3E";
										};

										target.src = powerStatsUrl;
									}}
								/>
							</Link>
						</div>
					</div>
				))}
				</div>
			</div>
		</div>
		
		{/* Veto Process - Show if veto data is available */}
		{vetoSteps.length > 0 && (
			<VetoSection vetoSteps={vetoSteps} mapPool={mapPool} />
		)}
		</>
	);
};

export default UpcomingMatchView;
